import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  XMarkIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  UsersIcon,
  TableCellsIcon,
  UserCircleIcon,
} from "@heroicons/react/24/solid";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import SearchBar from "../ui/SearchBar";
import { LoadingSpinner } from "../ui/LoadingSpinner";
import { PageLoadingView } from "../common/PageLoadingView";
import { PageErrorView } from "../common/PageErrorView";
// DataManagementSidebar was planned but a custom sidebar is used for now.
import RequestsTab from "../common/RequestsTab";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "../ui/table";
import SimpleDate from "../common/SimpleDate";
import ExpandedGuestProfileCard from "./ExpandedGuestProfileCard";
import type { GuestProfile } from "../../types/guest"; // Corrected: Only import GuestProfile
// import { Modal } from "../ui/modal"; // Removed as it will be used in GuestSelectionGridModal
import GuestSelectionGridModal from "./GuestSelectionGridModal";
import { useGuestProfiles } from "../../hooks/useGuestProfiles";
import {
  transformDirectoryItemToLegacyProfile,
  transformGuestDetailToLegacyProfile,
} from "../../types/guest";
import { formatDateOnlyForDisplay } from "../../utils/dateFormatting";
import { useAuthenticatedApi } from "../../hooks/useAuthenticatedApi";
import type { DiningOrderItem } from "../../types/request";
import type { Charge as BaseCharge } from "../../types/guest";
import { useNavigate } from "react-router-dom";

type GuestFilterStatus = "All" | "Current" | "Historic" | "Future";
type GuestViewMode = "table" | "expanded";

// Helper function to format status text by adding spaces before capital letters
const formatStatus = (status: string): string => {
  return status.replace(/([a-z])([A-Z])/g, "$1 $2");
};

// Map many backend reservation status variants to consistent badge styles
const getReservationStatusClasses = (rawStatus: string): string => {
  const base =
    "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
  const s = (rawStatus || "").toLowerCase().replace(/\s+/g, "");

  // Greens: in-house/arrived/checked-in
  if (
    s.includes("checkedin") ||
    s.includes("inhouse") ||
    s.includes("arrived")
  ) {
    return `${base} bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200`;
  }

  // Grays: departed/checked-out/checkedout
  if (s.includes("checkedout") || s.includes("departed")) {
    return `${base} bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200`;
  }

  // Reds: cancelled/canceled/no-show/noshow
  if (s.includes("cancel") || s.includes("noshow") || s.includes("no-show")) {
    return `${base} bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200`;
  }

  // Yellows/Oranges: due in / due out
  if (s.includes("duein")) {
    return `${base} bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200`;
  }
  if (s.includes("dueout")) {
    return `${base} bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200`;
  }

  // Blues: confirmed/booked/guaranteed/reserved
  if (
    s.includes("confirm") ||
    s.includes("booked") ||
    s.includes("guarantee") ||
    s.includes("reserve")
  ) {
    return `${base} bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200`;
  }

  // Purples: waitlist/hold/tentative
  if (s.includes("wait") || s.includes("tentative") || s.includes("hold")) {
    return `${base} bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200`;
  }

  // Fallback (informational)
  return `${base} bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200`;
};

const GuestProfiles: React.FC = () => {
  const navigate = useNavigate();

  // Use the real hook instead of dummy data
  const {
    guests,
    isLoading,
    error,
    pagination,
    activeFilter,
    searchQuery,
    selectedGuest: selectedGuestDetails,
    isLoadingDetails,
    detailsError,
    chargeDetails,
    actions: {
      fetchGuestDetails,
      fetchChargeDetails,
      setActiveFilter,
      setSearchQuery,
      setPage,
      setLimit,
      clearSelectedGuest,
      refetchAll,
    },
  } = useGuestProfiles();

  // Authenticated API for fetching modal guests (not charge details)
  const { callApi } = useAuthenticatedApi();

  // State for expanded charges and their items
  const [expandedCharges, setExpandedCharges] = useState<Set<string>>(
    new Set(),
  );
  const [chargeItemsMap, setChargeItemsMap] = useState<
    Record<string, DiningOrderItem[]>
  >({});
  const [isLoadingChargeMap, setIsLoadingChargeMap] = useState<
    Record<string, boolean>
  >({});
  const [chargeErrorMap, setChargeErrorMap] = useState<Record<string, string>>(
    {},
  );

  // Extract a valid UUID (xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx) from the description
  const extractRequestId = (description: string): string | null => {
    const uuidRegex =
      /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/;
    const match = description.match(uuidRegex);
    return match ? match[0] : null;
  };

  // Return human-readable charge description (strip IDs)
  const prettyDescription = (description: string): string => {
    // Remove UUID or plain hex id following the text
    return description
      .replace(
        /[\s-]*([0-9a-fA-F]{8}(-[0-9a-fA-F]{4}){1,4}|[0-9a-fA-F]{8,})/g,
        "",
      )
      .trim();
  };

  // Extend Charge type to optionally include requestId
  interface ChargeWithRequestId extends BaseCharge {
    requestId?: string;
  }

  const handleToggleCharge = async (charge: ChargeWithRequestId) => {
    const { chargeId, description } = charge;
    setExpandedCharges((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(chargeId)) newSet.delete(chargeId);
      else newSet.add(chargeId);
      return newSet;
    });

    if (chargeItemsMap[chargeId] || isLoadingChargeMap[chargeId]) return;

    const requestId = charge.requestId || extractRequestId(description);
    if (!requestId) return;

    try {
      setIsLoadingChargeMap((prev) => ({ ...prev, [chargeId]: true }));
      await fetchChargeDetails(requestId);

      // Extract order items from the charge details
      if (chargeDetails && chargeDetails.orderItems) {
        const items: DiningOrderItem[] = chargeDetails.orderItems;
        setChargeItemsMap((prev) => ({ ...prev, [chargeId]: items }));
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Failed to load order items";
      setChargeErrorMap((prev) => ({ ...prev, [chargeId]: message }));
    } finally {
      setIsLoadingChargeMap((prev) => ({ ...prev, [chargeId]: false }));
    }
  };

  // Local input state for the search bar (so we only submit on button / Enter)
  const [searchInput, setSearchInput] = useState<string>(searchQuery);

  // Keep local input synced when hook searchQuery changes externally
  useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);

  // Local UI state
  const [selectedGuest, setSelectedGuest] = useState<GuestProfile | null>(null);
  const [viewMode, setViewMode] = useState<GuestViewMode>("table");
  const [currentExpandedGuestIndex, setCurrentExpandedGuestIndex] =
    useState<number>(0);
  const [isGuestGridModalOpen, setIsGuestGridModalOpen] = useState(false);

  // Modal pagination state (separate from main pagination)
  const [modalPagination, setModalPagination] = useState({
    page: 1,
    limit: 20,
    totalCount: 0,
    totalPages: 0,
  });
  const [modalGuests, setModalGuests] = useState<GuestProfile[]>([]);
  const [isLoadingModalGuests, setIsLoadingModalGuests] = useState(false);

  // Transform backend data to legacy format for existing UI components
  const filteredProfiles = useMemo(() => {
    return guests.map(transformDirectoryItemToLegacyProfile);
  }, [guests]);

  // Track if we're currently selecting from modal to prevent auto-reset
  const isSelectingFromModalRef = useRef(false);

  // Effect to manage selectedGuest based on filteredProfiles and viewMode
  useEffect(() => {
    // If no profiles exist, always clear selection
    if (filteredProfiles.length === 0) {
      if (selectedGuest !== null) {
        setSelectedGuest(null);
        clearSelectedGuest();
      }
      return;
    }

    // At this point, filteredProfiles.length > 0
    const isSelectedGuestInFilteredList =
      selectedGuest &&
      filteredProfiles.some((p) => p.guestId === selectedGuest.guestId);

    if (viewMode === "expanded") {
      // In expanded view, if no valid guest is selected AND we're not actively selecting from modal, select the first one.
      if (!isSelectedGuestInFilteredList && !isSelectingFromModalRef.current) {
        setSelectedGuest(filteredProfiles[0]);
      }
    } else {
      // viewMode === 'table'
      // In table view, if a guest is selected but is no longer in the filtered list, clear the selection.
      // We do NOT automatically select a guest if selectedGuest is null, allowing sidebar to stay closed.
      if (
        selectedGuest &&
        !isSelectedGuestInFilteredList &&
        !isSelectingFromModalRef.current
      ) {
        setSelectedGuest(null);
        clearSelectedGuest();
      }
    }
  }, [filteredProfiles, viewMode, selectedGuest, clearSelectedGuest]);

  // Reset the modal selection flag after async operations complete
  useEffect(() => {
    if (isSelectingFromModalRef.current) {
      // Reset after a brief delay to allow the selection to complete
      const timer = setTimeout(() => {
        isSelectingFromModalRef.current = false;
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [selectedGuest]);

  // Effect to keep currentExpandedGuestIndex synchronized with selectedGuest and filteredProfiles.
  useEffect(() => {
    if (selectedGuest && filteredProfiles.length > 0) {
      const idx = filteredProfiles.findIndex(
        (p) => p.guestId === selectedGuest.guestId,
      );
      if (idx !== -1) {
        setCurrentExpandedGuestIndex(idx);
      }
    } else {
      setCurrentExpandedGuestIndex(0);
    }
  }, [selectedGuest, filteredProfiles]);

  // Effect to fetch detailed guest data when a guest is selected
  useEffect(() => {
    if (
      selectedGuest &&
      (!selectedGuestDetails ||
        selectedGuestDetails.guestId !== selectedGuest.guestId)
    ) {
      fetchGuestDetails(selectedGuest.guestId);
    }
  }, [selectedGuest, selectedGuestDetails, fetchGuestDetails]);

  // Transform detailed guest data for legacy components when available
  const transformedSelectedGuest = useMemo(() => {
    if (selectedGuestDetails) {
      return transformGuestDetailToLegacyProfile(selectedGuestDetails);
    }
    return selectedGuest;
  }, [selectedGuestDetails, selectedGuest]);

  const handleGuestSelect = (guest: GuestProfile) => {
    isSelectingFromModalRef.current = true;
    setSelectedGuest(guest);
    setIsGuestGridModalOpen(false);
  };

  const handleNavigateExpandedGuest = (direction: "next" | "prev") => {
    if (filteredProfiles.length === 0) return;
    let newIndex = currentExpandedGuestIndex;
    if (direction === "next") {
      newIndex = (currentExpandedGuestIndex + 1) % filteredProfiles.length;
    } else {
      newIndex =
        (currentExpandedGuestIndex - 1 + filteredProfiles.length) %
        filteredProfiles.length;
    }
    setCurrentExpandedGuestIndex(newIndex);
    setSelectedGuest(filteredProfiles[newIndex]);
  };

  // Fetch guests for modal with pagination
  const fetchModalGuests = async (page: number = 1) => {
    setIsLoadingModalGuests(true);
    try {
      const response = await callApi({
        method: "GET",
        url: `/management/guests?page=${page}&limit=20&status=${activeFilter}${
          searchQuery.trim()
            ? `&search=${encodeURIComponent(searchQuery.trim())}`
            : ""
        }`,
      });

      if (response.data && response.data.data) {
        const transformedGuests = response.data.data.map(
          transformDirectoryItemToLegacyProfile,
        );
        setModalGuests(transformedGuests);
        setModalPagination({
          page: response.data.meta?.page || 1,
          limit: 20,
          totalCount: response.data.meta?.totalCount || 0,
          totalPages: response.data.meta?.totalPages || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching modal guests:", error);
      setModalGuests([]);
    } finally {
      setIsLoadingModalGuests(false);
    }
  };

  const handleShowGuestGrid = () => {
    setIsGuestGridModalOpen(true);
    fetchModalGuests(1); // Reset to page 1 when opening modal
  };

  const handleModalPageChange = (newPage: number) => {
    fetchModalGuests(newPage);
  };

  // Navigate to specific request page with deep link
  const handleRequestClick = (
    requestType: string,
    requestId: string,
    status?: string,
  ) => {
    let targetPath = "";
    let statusTabParam = "";

    // Map request types to their corresponding pages
    switch (requestType.toLowerCase()) {
      case "general":
        targetPath = "/general";
        break;
      case "dining":
      case "in-room dining":
        targetPath = "/in-room-dining";
        break;
      case "reservation":
        targetPath = "/reservations";
        break;
      default:
        // Default to general requests for unknown types
        targetPath = "/general";
    }

    // Map raw status to tab for each page
    const normalized = (status || "").toLowerCase();
    if (targetPath === "/general" || targetPath === "/in-room-dining") {
      // Active, Scheduled, History
      if (normalized === "scheduled") statusTabParam = "Scheduled";
      else if (normalized === "completed" || normalized === "cancelled")
        statusTabParam = "History";
      else statusTabParam = "Active";
    } else if (targetPath === "/reservations") {
      // New (Submitted) or Completed
      if (normalized === "completed") statusTabParam = "Completed";
      else statusTabParam = "New";
    }

    // Navigate with requestId and statusTab parameters for deep linking
    const search = new URLSearchParams({ requestId });
    if (statusTabParam) search.set("statusTab", statusTabParam);
    navigate(`${targetPath}?${search.toString()}`);
  };

  // Pagination handlers
  const handlePageChange = (newPage: number) => {
    if (
      pagination.totalCount > 0 && // Add this check to prevent issues when total is 0
      (newPage < 1 ||
        newPage > Math.ceil(pagination.totalCount / pagination.limit))
    ) {
      return;
    }
    setPage(newPage);
  };

  const handleLimitChange = (newLimit: number) => {
    if (![10, 20, 50].includes(newLimit)) {
      return;
    }
    setLimit(newLimit);
  };

  if (isLoading) {
    return <PageLoadingView />;
  }

  if (error) {
    return (
      <PageErrorView
        title="Error loading guest profiles"
        onRetry={refetchAll}
      />
    );
  }

  return (
    <div className="w-full h-screen flex flex-col text-gray-800 dark:text-white overflow-hidden">
      <div className="p-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Guest Profiles</h1>
        <div className="shadow-theme-xs inline-flex items-center">
          <button
            type="button"
            onClick={() => {
              setViewMode("table");
              setSelectedGuest(null);
              clearSelectedGuest();
            }}
            className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-medium ring-1 ring-inset first:rounded-l-lg last:rounded-r-lg transition
                        ${
                          viewMode === "table"
                            ? "bg-blue-600 text-white ring-blue-600 hover:bg-blue-700"
                            : "bg-transparent text-blue-600 ring-blue-600 hover:bg-blue-500 hover:text-white dark:text-blue-400 dark:ring-blue-500 dark:hover:bg-blue-600 dark:hover:text-white"
                        }`}
          >
            <TableCellsIcon className="w-5 h-5" />
            Table View
          </button>
          <button
            type="button"
            onClick={() => setViewMode("expanded")}
            className={`-ml-px inline-flex items-center gap-2 px-4 py-3 text-sm font-medium ring-1 ring-inset first:rounded-l-lg last:rounded-r-lg transition
                        ${
                          viewMode === "expanded"
                            ? "bg-blue-600 text-white ring-blue-600 hover:bg-blue-700"
                            : "bg-transparent text-blue-600 ring-blue-600 hover:bg-blue-500 hover:text-white dark:text-blue-400 dark:ring-blue-500 dark:hover:bg-blue-600 dark:hover:text-white"
                        }`}
          >
            <UserCircleIcon className="w-5 h-5" />
            Profile View
          </button>
        </div>
      </div>

      <div className="px-6 mb-4">
        <RequestsTab
          onFrameChange={(frame) => setActiveFilter(frame as GuestFilterStatus)}
          selectedFrame={activeFilter}
          options={["All", "Current", "Historic", "Future"]}
        />
      </div>

      <div className="px-6">
        <SearchBar
          searchQuery={searchInput}
          onSearchChange={(q) => {
            setSearchInput(q);
            if (q === "") {
              // Clear backend search instantly when input cleared
              setSearchQuery("");
            }
          }}
          onSearchSubmit={() => setSearchQuery(searchInput)}
          isLoading={isLoading}
          placeholder="Search by name, room, or email..."
        />
      </div>

      {viewMode === "table" && (
        <div className="flex-1 overflow-y-auto px-6 pb-6">
          {filteredProfiles.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              {searchQuery
                ? "No guests match your search"
                : `No ${activeFilter.toLowerCase()} guests found`}
            </div>
          ) : (
            <Table className="w-full">
              <TableHeader className="sticky top-0 z-20 bg-gray-50 dark:bg-gray-800 border-b-[3px] border-gray-200 dark:border-gray-700 shadow-sm">
                <TableRow>
                  <TableCell
                    isHeader
                    className="py-3 pl-2 pr-1 font-medium text-sm text-gray-700 dark:text-gray-200 text-left w-2/6"
                  >
                    Name
                  </TableCell>
                  <TableCell
                    isHeader
                    className="py-3 px-1 font-medium text-sm text-gray-700 dark:text-gray-200 text-left w-1/6"
                  >
                    Room
                  </TableCell>
                  <TableCell
                    isHeader
                    className="py-3 px-1 font-medium text-sm text-gray-700 dark:text-gray-200 text-left w-1/6"
                  >
                    Check-in
                  </TableCell>
                  <TableCell
                    isHeader
                    className="py-3 px-1 font-medium text-sm text-gray-700 dark:text-gray-200 text-left w-1/6"
                  >
                    Check-out
                  </TableCell>
                  <TableCell
                    isHeader
                    className="py-3 pl-1 pr-2 font-medium text-sm text-gray-700 dark:text-gray-200 text-left w-1/6"
                  >
                    Status
                  </TableCell>
                </TableRow>
              </TableHeader>
              <TableBody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredProfiles.map((guest) => (
                  <TableRow
                    key={guest.guestId}
                    className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/60 ${
                      selectedGuest?.guestId === guest.guestId
                        ? "bg-gray-100 dark:bg-gray-700/50"
                        : ""
                    }`}
                    onClick={() => handleGuestSelect(guest)}
                  >
                    <TableCell className="py-3 pl-2 pr-1 text-sm text-gray-700 dark:text-gray-200">
                      {guest.name || "N/A"}
                    </TableCell>
                    <TableCell className="py-3 px-1 text-sm text-gray-500 dark:text-gray-400">
                      {guest.room.roomNumber}
                    </TableCell>
                    <TableCell className="py-3 px-1 text-sm text-gray-500 dark:text-gray-400">
                      {guest.checkInDate ? (
                        <SimpleDate date={guest.checkInDate} />
                      ) : (
                        "N/A"
                      )}
                    </TableCell>
                    <TableCell className="py-3 px-1 text-sm text-gray-500 dark:text-gray-400">
                      {guest.checkOutDate ? (
                        <SimpleDate date={guest.checkOutDate} />
                      ) : (
                        "N/A"
                      )}
                    </TableCell>
                    <TableCell className="py-3 pl-1 pr-2 text-sm text-gray-500 dark:text-gray-400">
                      <span
                        className={getReservationStatusClasses(guest.status)}
                      >
                        {(() => {
                          const normalized = (guest.status || "")
                            .toLowerCase()
                            .replace(/\s+/g, "");
                          if (normalized === "roomreserved") {
                            return (
                              <>
                                <span className="hidden sm:inline">
                                  Room Reserved
                                </span>
                                <span className="inline sm:hidden">
                                  Room Res.
                                </span>
                              </>
                            );
                          }
                          return formatStatus(guest.status);
                        })()}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      )}

      {/* Pagination Controls for Table View */}
      {viewMode === "table" &&
        filteredProfiles.length > 0 &&
        pagination.totalCount > 0 && (
          <div className="py-4 mt-auto flex items-center justify-between sticky bottom-0 z-10 border-t border-gray-200 dark:border-gray-700 px-6 bg-gray-50 dark:bg-gray-900">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                Show
              </span>
              <select
                value={pagination.limit}
                onChange={(e) => handleLimitChange(Number(e.target.value))}
                className="border rounded px-3 py-1 text-sm bg-white dark:bg-gray-800 dark:text-white pr-8 focus:ring-blue-500 focus:border-blue-500 dark:focus:ring-blue-400 dark:focus:border-blue-400"
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
              </select>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                guests per page
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-900"
              >
                Previous
              </button>
              <span className="text-sm text-gray-700 dark:text-gray-300">
                Page {pagination.page} of{" "}
                {Math.ceil(pagination.totalCount / pagination.limit) || 1}
              </span>
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={
                  pagination.page >=
                  Math.ceil(pagination.totalCount / pagination.limit)
                }
                className="px-3 py-1 border rounded disabled:opacity-50 hover:bg-gray-50 dark:hover:bg-gray-700 dark:text-white dark:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:focus:ring-offset-gray-900"
              >
                Next
              </button>
            </div>
          </div>
        )}

      {viewMode === "expanded" && (
        <div className="flex-1 overflow-y-auto p-6">
          {transformedSelectedGuest && filteredProfiles.length > 0 ? (
            <div>
              <div className="mb-6 flex justify-between items-center pb-4 border-b border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => handleNavigateExpandedGuest("prev")}
                  disabled={filteredProfiles.length <= 1}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600 disabled:opacity-50"
                >
                  <ChevronLeftIcon className="w-5 h-5 inline mr-1" /> Previous
                </button>
                <button
                  onClick={handleShowGuestGrid}
                  className="px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-300 rounded-md shadow-sm hover:bg-blue-100 dark:bg-blue-900/50 dark:text-blue-300 dark:border-blue-700 dark:hover:bg-blue-800/50"
                >
                  <UsersIcon className="w-5 h-5 inline mr-1" /> Select Guest (
                  {currentExpandedGuestIndex + 1} of {pagination.totalCount})
                </button>
                <button
                  onClick={() => handleNavigateExpandedGuest("next")}
                  disabled={filteredProfiles.length <= 1}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-600 disabled:opacity-50"
                >
                  Next <ChevronRightIcon className="w-5 h-5 inline ml-1" />
                </button>
              </div>

              {isLoadingDetails ? (
                <div className="flex justify-center items-center py-8">
                  <LoadingSpinner size="md" color="primary" />
                </div>
              ) : detailsError ? (
                <PageErrorView
                  title="Error loading guest details"
                  onRetry={() =>
                    selectedGuest && fetchGuestDetails(selectedGuest.guestId)
                  }
                />
              ) : (
                <ExpandedGuestProfileCard guest={transformedSelectedGuest} />
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <p>
                {filteredProfiles.length === 0
                  ? `No ${activeFilter.toLowerCase()} guests found`
                  : "Please select a guest to view their profile or switch to table view."}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Table view sidebar */}
      {viewMode === "table" && (
        <>
          {/* Backdrop: click outside sidebar (including header/app nav) to close */}
          {selectedGuest && (
            <div
              aria-hidden
              className="fixed inset-0 z-10 top-0 left-0 right-0 bottom-0"
              onClick={() => {
                setSelectedGuest(null);
                clearSelectedGuest();
              }}
            />
          )}
          <div
            className={`
              fixed right-0 top-[70px] w-[35%] h-[calc(100vh-70px)] 
              shadow-lg bg-white dark:bg-black z-20 
              transition-transform duration-300 ease-in-out transform
              overflow-y-auto
              ${selectedGuest ? "translate-x-0" : "translate-x-full"}
            `}
          >
            {selectedGuest && (
              <div className="p-6 space-y-6">
                <div className="flex justify-between items-start mb-2">
                  <h2 className="text-xl font-semibold">Guest Details</h2>
                  <button
                    onClick={() => {
                      setSelectedGuest(null);
                      clearSelectedGuest();
                    }}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    <XMarkIcon className="w-6 h-6" />
                  </button>
                </div>

                {isLoadingDetails ? (
                  <div className="flex justify-center items-center py-8">
                    <LoadingSpinner size="md" color="primary" />
                  </div>
                ) : detailsError ? (
                  <PageErrorView
                    title="Error loading guest details"
                    onRetry={() =>
                      selectedGuest && fetchGuestDetails(selectedGuest.guestId)
                    }
                  />
                ) : transformedSelectedGuest ? (
                  <>
                    <section>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        Identity
                      </h3>
                      <div className="text-sm space-y-1">
                        <p>
                          <span className="font-medium text-gray-600 dark:text-gray-400">
                            Name:
                          </span>{" "}
                          {transformedSelectedGuest.title
                            ? `${transformedSelectedGuest.title} ${transformedSelectedGuest.name}`
                            : transformedSelectedGuest.name}
                        </p>
                        <p>
                          <span className="font-medium text-gray-600 dark:text-gray-400">
                            Guest ID:
                          </span>{" "}
                          {transformedSelectedGuest.guestId}
                        </p>
                        {transformedSelectedGuest.gender && (
                          <p>
                            <span className="font-medium text-gray-600 dark:text-gray-400">
                              Gender:
                            </span>{" "}
                            {transformedSelectedGuest.gender}
                          </p>
                        )}
                        {transformedSelectedGuest.dateOfBirth && (
                          <p>
                            <span className="font-medium text-gray-600 dark:text-gray-400">
                              Date of Birth:
                            </span>{" "}
                            {formatDateOnlyForDisplay(
                              transformedSelectedGuest.dateOfBirth,
                            )}
                          </p>
                        )}
                        <p>
                          <span className="font-medium text-gray-600 dark:text-gray-400">
                            Email:
                          </span>{" "}
                          {transformedSelectedGuest.email || "N/A"}
                        </p>
                        <p>
                          <span className="font-medium text-gray-600 dark:text-gray-400">
                            Phone:
                          </span>{" "}
                          {transformedSelectedGuest.phone || "N/A"}
                        </p>
                      </div>
                    </section>

                    <div className="border-t border-gray-200 dark:border-gray-700"></div>

                    <section>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        Stay Information
                      </h3>
                      <div className="text-sm space-y-1">
                        <p>
                          <span className="font-medium text-gray-600 dark:text-gray-400">
                            Status:
                          </span>{" "}
                          <span
                            className={getReservationStatusClasses(
                              transformedSelectedGuest.status,
                            )}
                          >
                            {(() => {
                              const normalized = (
                                transformedSelectedGuest.status || ""
                              )
                                .toLowerCase()
                                .replace(/\s+/g, "");
                              if (normalized === "roomreserved") {
                                return (
                                  <>
                                    <span className="hidden sm:inline">
                                      Room Reserved
                                    </span>
                                    <span className="inline sm:hidden">
                                      Room Res.
                                    </span>
                                  </>
                                );
                              }
                              return formatStatus(transformedSelectedGuest.status);
                            })()}
                          </span>
                        </p>
                        <p>
                          <span className="font-medium text-gray-600 dark:text-gray-400">
                            Check-in:
                          </span>{" "}
                          {transformedSelectedGuest.checkInDate ? (
                            <SimpleDate
                              date={transformedSelectedGuest.checkInDate}
                            />
                          ) : (
                            "N/A"
                          )}
                        </p>
                        <p>
                          <span className="font-medium text-gray-600 dark:text-gray-400">
                            Check-out:
                          </span>{" "}
                          {transformedSelectedGuest.checkOutDate ? (
                            <SimpleDate
                              date={transformedSelectedGuest.checkOutDate}
                            />
                          ) : (
                            "N/A"
                          )}
                        </p>
                      </div>
                    </section>

                    <div className="border-t border-gray-200 dark:border-gray-700"></div>

                    <section>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        Accompanying Guests
                      </h3>
                      {transformedSelectedGuest.otherGuests.length > 0 ? (
                        <ul className="space-y-2">
                          {transformedSelectedGuest.otherGuests.map((og) => (
                            <li
                              key={og.guestId}
                              className="p-2 border border-gray-200 dark:border-gray-700 rounded-md"
                            >
                              <p className="font-medium text-gray-800 dark:text-gray-100">
                                {og.name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                ID: {og.guestId}
                              </p>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          No other guests listed.
                        </p>
                      )}
                    </section>

                    <div className="border-t border-gray-200 dark:border-gray-700"></div>

                    <section>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        Billing
                      </h3>
                      <h4 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Recent Charges
                      </h4>
                      {transformedSelectedGuest.recentCharges.length > 0 ? (
                        <div className="border border-gray-200 dark:border-gray-700 rounded-md max-h-64 overflow-y-auto">
                          <Table className="w-full text-sm">
                            <TableBody>
                              {transformedSelectedGuest.recentCharges.map(
                                (charge) => (
                                  <React.Fragment key={charge.chargeId}>
                                    <TableRow className="border-b border-gray-200 dark:border-gray-700 last:border-b-0 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                                      <TableCell className="p-2 w-6">
                                        <button
                                          onClick={() =>
                                            handleToggleCharge(charge)
                                          }
                                          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                                        >
                                          <ChevronDownIcon
                                            className={`w-4 h-4 transition-transform ${
                                              expandedCharges.has(
                                                charge.chargeId,
                                              )
                                                ? "rotate-180"
                                                : ""
                                            }`}
                                          />
                                        </button>
                                      </TableCell>
                                      <TableCell className="p-2 text-gray-700 dark:text-gray-200">
                                        {prettyDescription(charge.description)}{" "}
                                        <span className="text-xs text-gray-400 dark:text-gray-500">
                                          (<SimpleDate date={charge.date} />)
                                        </span>
                                      </TableCell>
                                      <TableCell className="p-2 text-gray-700 dark:text-gray-200 text-right">
                                        ${charge.amount.toFixed(2)}
                                      </TableCell>
                                    </TableRow>
                                    {expandedCharges.has(charge.chargeId) && (
                                      <TableRow className="bg-gray-50 dark:bg-gray-900/40">
                                        <td colSpan={3} className="p-2">
                                          {isLoadingChargeMap[
                                            charge.chargeId
                                          ] ? (
                                            <div className="flex items-center justify-center py-2">
                                              <LoadingSpinner
                                                size="sm"
                                                color="primary"
                                              />
                                            </div>
                                          ) : chargeErrorMap[
                                              charge.chargeId
                                            ] ? (
                                            <p className="text-sm text-red-500">
                                              {chargeErrorMap[charge.chargeId]}
                                            </p>
                                          ) : chargeItemsMap[charge.chargeId] &&
                                            chargeItemsMap[charge.chargeId]
                                              .length > 0 ? (
                                            <Table className="w-full text-xs">
                                              <TableHeader>
                                                <TableRow>
                                                  <TableCell className="py-1 font-medium text-gray-600 dark:text-gray-400">
                                                    Item
                                                  </TableCell>
                                                  <TableCell className="py-1 font-medium text-gray-600 dark:text-gray-400 text-center w-16">
                                                    Qty
                                                  </TableCell>
                                                  <TableCell className="py-1 font-medium text-gray-600 dark:text-gray-400 text-right">
                                                    Price
                                                  </TableCell>
                                                </TableRow>
                                              </TableHeader>
                                              <TableBody>
                                                {chargeItemsMap[
                                                  charge.chargeId
                                                ].map((item) => (
                                                  <TableRow
                                                    key={item.orderItemId}
                                                    className="border-b border-gray-200 dark:border-gray-700 last:border-b-0"
                                                  >
                                                    <TableCell className="py-1 text-gray-700 dark:text-gray-200">
                                                      {item.itemName}
                                                    </TableCell>
                                                    <TableCell className="py-1 text-center text-gray-700 dark:text-gray-200">
                                                      {item.quantity}
                                                    </TableCell>
                                                    <TableCell className="py-1 text-right text-gray-700 dark:text-gray-200">
                                                      {item.price
                                                        .currencyCode === "USD"
                                                        ? "$"
                                                        : item.price
                                                            .currencyCode + " "}
                                                      {parseFloat(
                                                        item.price.amount,
                                                      ).toFixed(2)}
                                                    </TableCell>
                                                  </TableRow>
                                                ))}
                                              </TableBody>
                                            </Table>
                                          ) : (
                                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                              No items found.
                                            </p>
                                          )}
                                        </td>
                                      </TableRow>
                                    )}
                                  </React.Fragment>
                                ),
                              )}
                            </TableBody>
                          </Table>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          No recent charges.
                        </p>
                      )}
                      {transformedSelectedGuest.recentCharges.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
                          <p className="text-md font-semibold text-gray-800 dark:text-gray-100">
                            Total Bill:
                          </p>
                          <p className="text-lg font-bold text-gray-900 dark:text-white">
                            ${transformedSelectedGuest.totalBill.toFixed(2)}
                          </p>
                        </div>
                      )}
                    </section>

                    {transformedSelectedGuest.recentCharges.length > 0 && (
                      <div className="border-t border-gray-200 dark:border-gray-700"></div>
                    )}

                    <section>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        Preferences
                      </h3>
                      {/* Dietary Restrictions */}
                      {transformedSelectedGuest.preferencesDietaryRestrictions &&
                        transformedSelectedGuest.preferencesDietaryRestrictions
                          .length > 0 && (
                          <div className="mb-4">
                            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                              Dietary Restrictions
                            </h4>
                            <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-200">
                              {transformedSelectedGuest.preferencesDietaryRestrictions.map(
                                (pref) => (
                                  <li key={`diet-${pref.dietaryRestrictionId}`}>
                                    {pref.name}
                                  </li>
                                ),
                              )}
                            </ul>
                          </div>
                        )}

                      {/* Accessibility Needs */}
                      {transformedSelectedGuest.preferencesAccessibilityNeeds &&
                        transformedSelectedGuest.preferencesAccessibilityNeeds
                          .length > 0 && (
                          <div className="mb-4">
                            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                              Accessibility Needs
                            </h4>
                            <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-200">
                              {transformedSelectedGuest.preferencesAccessibilityNeeds.map(
                                (pref, idx) => (
                                  <li key={`access-${idx}`}>
                                    {pref
                                      .replace(/_/g, " ")
                                      .replace(/\b\w/g, (l) => l.toUpperCase())}
                                  </li>
                                ),
                              )}
                            </ul>
                          </div>
                        )}

                      {/* Communication Preferences */}
                      {transformedSelectedGuest.preferencesCommunication &&
                        transformedSelectedGuest.preferencesCommunication
                          .length > 0 && (
                          <div className="mb-4">
                            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                              Communication Preferences
                            </h4>
                            <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-200">
                              {transformedSelectedGuest.preferencesCommunication.map(
                                (pref, idx) => (
                                  <li key={`comm-${idx}`}>
                                    {pref
                                      .replace(/_/g, " ")
                                      .replace(/\b\w/g, (l) => l.toUpperCase())}
                                  </li>
                                ),
                              )}
                            </ul>
                          </div>
                        )}

                      {/* General Stay and Interests */}
                      {transformedSelectedGuest.preferencesGeneral &&
                        transformedSelectedGuest.preferencesGeneral.length >
                          0 && (
                          <div className="mb-4">
                            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                              General Stay and Interests
                            </h4>
                            <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-200">
                              {transformedSelectedGuest.preferencesGeneral.map(
                                (pref) => (
                                  <li key={`gen-${pref}`}>{pref}</li>
                                ),
                              )}
                            </ul>
                          </div>
                        )}

                      {/* Interests & Passions */}
                      {transformedSelectedGuest.preferencesHotelInterests &&
                        transformedSelectedGuest.preferencesHotelInterests
                          .length > 0 && (
                          <div className="mb-4">
                            <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                              Interests & Passions
                            </h4>
                            <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-200">
                              {transformedSelectedGuest.preferencesHotelInterests.map(
                                (pref) => (
                                  <li key={`hotel-${pref}`}>{pref}</li>
                                ),
                              )}
                            </ul>
                          </div>
                        )}
                    </section>

                    <div className="border-t border-gray-200 dark:border-gray-700"></div>

                    <section>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        Room Information
                      </h3>
                      <div className="text-sm space-y-1">
                        <p>
                          <span className="font-medium text-gray-600 dark:text-gray-400">
                            Room No.:
                          </span>{" "}
                          {transformedSelectedGuest.room.roomNumber}
                        </p>
                        <p>
                          <span className="font-medium text-gray-600 dark:text-gray-400">
                            Type:
                          </span>{" "}
                          {transformedSelectedGuest.room.type}
                        </p>
                        <p>
                          <span className="font-medium text-gray-600 dark:text-gray-400">
                            Floor:
                          </span>{" "}
                          {transformedSelectedGuest.room.floor}
                        </p>
                        <p>
                          <span className="font-medium text-gray-600 dark:text-gray-400">
                            Room ID:
                          </span>{" "}
                          {transformedSelectedGuest.room.roomId}
                        </p>
                      </div>
                    </section>

                    <div className="border-t border-gray-200 dark:border-gray-700"></div>

                    <section>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        Request History
                      </h3>
                      {transformedSelectedGuest.requestHistory.length > 0 ? (
                        <ul className="space-y-2 max-h-64 overflow-y-auto">
                          {transformedSelectedGuest.requestHistory.map(
                            (req) => (
                              <li
                                key={req.requestId}
                                className="p-2 border border-gray-200 dark:border-gray-700 rounded-md cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                                onClick={() =>
                                  handleRequestClick(
                                    req.requestType,
                                    req.requestId,
                                    req.status,
                                  )
                                }
                              >
                                <p className="font-medium text-gray-800 dark:text-gray-100">
                                  {req.requestType}{" "}
                                  <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                                    ({formatStatus(req.status)})
                                  </span>
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  ID: {req.requestId} -{" "}
                                  <SimpleDate date={req.submittedAt} />
                                </p>
                                <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                                  Click to view request details
                                </p>
                              </li>
                            ),
                          )}
                        </ul>
                      ) : (
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          No request history.
                        </p>
                      )}
                    </section>

                    <div className="pt-4">
                      {" "}
                      {/* Extra padding at the bottom of sidebar */}
                    </div>
                  </>
                ) : null}
              </div>
            )}
          </div>
        </>
      )}

      <GuestSelectionGridModal
        isOpen={isGuestGridModalOpen}
        onClose={() => setIsGuestGridModalOpen(false)}
        guests={modalGuests}
        onGuestSelect={handleGuestSelect}
        currentGuestId={selectedGuest?.guestId}
        pagination={modalPagination}
        onPageChange={handleModalPageChange}
        isLoading={isLoadingModalGuests}
      />
    </div>
  );
};

export default GuestProfiles;
